import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { Fade } from "react-awesome-reveal";

/**
 * Hero5 - Partners / OTAs carousel (sin dependencias externas)
 * - Dos filas en “marquee” infinito (CSS keyframes)
 * - Pausa al hover
 * - Gradiente en bordes para un fade sutil
 * - Accesible (alt + aria-hidden donde corresponde)
 */

const LOGOS_ROW_1 = [
  { name: "Booking.com" },
  { name: "Airbnb" },
  { name: "Hostelworld" },
  { name: "Expedia" },
  { name: "Agoda" },
  { name: "Vrbo" },
];

const LOGOS_ROW_2 = [
  { name: "Google Hotel Ads" },
  { name: "Trip.com" },
  { name: "HRS" },
  { name: "Traveloka" },
  { name: "Ctrip" },
  { name: "Despegar" },
];

function Hero5() {
  return (
    <Box
      sx={{
        position: "relative",
        py: { xs: 6, md: 8 },
        background: "linear-gradient(180deg, #903AF2 0%, #6A1FBF 100%)",
        color: "#fff",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Fade triggerOnce>
          <Typography
            sx={{
              textAlign: "center",
              fontFamily: "'Red Hat Display', sans-serif",
              fontWeight: 900,
              fontSize: { xs: "1.6rem", md: "2rem" },
              textShadow: "0 1px 2px rgba(0,0,0,0.25)",
            }}
          >
            Partners que confían y se conectan con Ubika
          </Typography>
          <Typography
            sx={{
              mt: 1,
              textAlign: "center",
              opacity: 0.95,
              fontFamily: "'Merriweather Sans', sans-serif",
              fontSize: { xs: "1rem", md: "1.05rem" },
            }}
          >
            Integramos con las principales OTAs del mercado.
          </Typography>
        </Fade>

        {/* Carrusel 1 */}
        <MarqueeRow items={LOGOS_ROW_1} reverse={false} speed="40s" mt={4} />

        {/* Carrusel 2 (opuesto) */}
        <MarqueeRow items={LOGOS_ROW_2} reverse speed="42s" mt={2} />
      </Container>

      {/* Fades laterales */}
      <Box
        aria-hidden
        sx={{
          pointerEvents: "none",
          position: "absolute",
          inset: 0,
          "&::before, &::after": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            width: { xs: 40, md: 80 },
            zIndex: 2,
          },
          "&::before": {
            left: 0,
            background:
              "linear-gradient(90deg, rgba(144,58,242,1) 0%, rgba(144,58,242,0) 100%)",
          },
          "&::after": {
            right: 0,
            background:
              "linear-gradient(270deg, rgba(106,31,191,1) 0%, rgba(106,31,191,0) 100%)",
          },
        }}
      />
    </Box>
  );
}

function MarqueeRow({ items, reverse = false, speed = "40s", mt = 0 }) {
  // Duplicamos el contenido para que el loop sea perfecto
  const doubled = [...items, ...items];

  return (
    <Box
      sx={{
        mt,
        overflow: "hidden",
        maskImage:
          "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
      }}
    >
      <Box
        className="marquee-track"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 4, md: 6 },
          animation: `${
            reverse ? "scrollReverse" : "scroll"
          } ${speed} linear infinite`,
          // Pausa al hover
          "&:hover": { animationPlayState: "paused" },
          "@keyframes scroll": {
            "0%": { transform: "translateX(0)" },
            "100%": { transform: "translateX(-50%)" },
          },
          "@keyframes scrollReverse": {
            "0%": { transform: "translateX(-50%)" },
            "100%": { transform: "translateX(0)" },
          },
        }}
      >
        {doubled.map((item, idx) => (
          <LogoItem key={`${item.name}-${idx}`} {...item} />
        ))}
      </Box>
    </Box>
  );
}

function LogoItem({ name }) {
  return (
    <Typography
      component="span"
      sx={{
        fontSize: { xs: "0.95rem", md: "1.1rem" },
        fontWeight: 600,
        color: "#fff",
        opacity: 0.9,
        whiteSpace: "nowrap",
        transition: "opacity 0.2s ease",
        "&:hover": { opacity: 1 },
      }}
    >
      {name}
    </Typography>
  );
}

export default Hero5;
